import React, {useCallback, useRef} from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Box from "@mui/material/Box";
import CircularProgress from '@mui/material/CircularProgress';
import {buildChartTooltip, updateChartTheme, valueFormatted} from "../../../utils/helpsers";
import logoSignature from "../../../IB_Signature.png";
import { ScatterStyles } from "./ScatterChart.styles";
import PropTypes from "prop-types";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import TooltipCustom from "../../molecules/CustomTooltip/CustomTooltip";

require("highcharts/modules/accessibility")(Highcharts);


/** Scatter chart using highcharts library.
* @param {object} x - X Axis of the chart.
* @param {object, array} xCustomRange - Custom Range of X axis.
* @param {object} y - Y Axis of the chart..
* @param {object, array} yCustomRange - Custom Range of Y axis.
* @param {object, array} options - Districts available.
*/

const ScatterChart = ({x, y, xCustomRange, yCustomRange, options}) => {
  let xDescription = x.description;
  let yDescription = y.description;
  let xAxisTitle = x.label;
  let yAxisTitle = y.label;
  
  xAxisTitle = xDescription
    ? `<span>${xAxisTitle} </span><span class="info-icon" id="scatter-axis-x">ⓘ</span><span class="tooltip-desc" style="visibility: hidden">${xDescription}</span>`
    : xAxisTitle;
  yAxisTitle = yDescription
    ? `<span>${yAxisTitle} </span><span class="info-icon" id="scatter-axis-y">ⓘ</span><span class="tooltip-desc" style="visibility: hidden">${yDescription}</span>`
    : yAxisTitle;

  const attachTooltipDescription = useCallback(()=> {
    if (xDescription) {
      let xInfoIcon = document.querySelector(".highcharts-axis #scatter-axis-x");
      buildChartTooltip({ infoIcon: xInfoIcon });
    }
    if (yDescription) {
      let yInfoIcon = document.querySelector(".highcharts-axis #scatter-axis-y");
      buildChartTooltip({ infoIcon: yInfoIcon, styles:"left: 100%; transform-origin: top left; transform: rotate(90deg);" });
    }
  },[xDescription, yDescription])

  // Create a ref to store the chart instance
  const chartRef = useRef(null);

  let logoCredit;
  const ScatterData = {
    chart: {
      type: "scatter",
      height: 620,
      backgroundColor: '#fff',
      plotBorderWidth: 1,
      zoomType: "xy",
      events: {
        render: function () {
          let points = this.series[0].points;
          points.forEach((point) => {
            if (point.selected === true) {
              this.tooltip.refresh(point);
            }
          });
          // Building Tooltips
          attachTooltipDescription();
        },
        load: function () {
          if (this.options.chart.forExport) {
            this.renderer.image(`${logoSignature}`, 10, 600, 150, 20).add();
          }
        },
        beforePrint: function () {
          logoCredit = this.renderer
            .image(`${logoSignature}`, 10, 600, 150, 20)
            .add();
          this.print();
        },
        afterPrint: function () {
          logoCredit.element.remove();
        },
      },
      style: {
        fontFamily: ["Titillium Web", "sans-serif"],
      },
      className: 'highcharts--scatter-chart'
    },
    title: {
      text: "",
    },
    subtitle: {
      text: "",
    },
    legend: {
      enabled: false,
    },
    accessibility: {
      point: {
        valueDescriptionFormat:
          "{index}. {point.name}, fat: {point.x}g, sugar: {point.y}g, obesity: {point.z}%.",
      },
    },
    xAxis: {
      gridLineWidth: 1,
      title: {
        useHTML: true,
        text: xAxisTitle,
        style: {
          color: "#333",
        }
      },
      labels: {
        format: "{value}",
        style: {
          color: "#333",
        }
      },
      accessibility: {
        rangeDescription: "",
      },
      plotLines: [
        {
          dashStyle: "shortdash",
          value: xCustomRange ? xCustomRange[0] : 0,
          width: xCustomRange ? 2 : 0,
          color: "#00D7BE",
        },
        {
          dashStyle: "shortdash",
          value: xCustomRange ? xCustomRange[1] : 0,
          width: xCustomRange ? 2 : 0,
          color: "#00D7BE",
        },
      ],
    },
    yAxis: {
      startOnTick: false,
      endOnTick: false,
      title: {
        useHTML: true,
        text: yAxisTitle,
        style: {
          color: "#333",
        }
      },
      labels: {
        format: "{value}",
        style: {
          color: "#333",
        }
      },
      maxPadding: 0.2,
      accessibility: {
        rangeDescription: "",
      },
      plotLines: [
        {
          dashStyle: "shortdash",
          value: yCustomRange ? yCustomRange[0] : 0,
          width: yCustomRange ? 2 : 0,
          color: "#00D7BE",
        },
        {
          dashStyle: "shortdash",
          value: yCustomRange ? yCustomRange[1] : 0,
          width: yCustomRange ? 2 : 0,
          color: "#00D7BE",
        },
      ],
    },
    tooltip: {
      useHTML: true,
      headerFormat: "<div>",
      pointFormatter: function () {
        let xValue = valueFormatted(this.x, x.var_type);
        let yValue = valueFormatted(this.y, y.var_type);
        return `<div><strong>${this.district_name}</strong></div>
                <div><span>${x.label}: </span><strong>${xValue}</strong></div>
                <div><span>${y.label}: </span><strong>${yValue}</strong></div>`;
      },
      footerFormat: "</div>",
      followPointer: true,
      backgroundColor: "#FFF",
      hideDelay: 0,
    },
    exporting: {
      tableCaption: false,
      buttons: {
        contextButton: {
          menuItems: [
            "viewFullscreen",
            {
              text: "Toggle Light/Dark mode",
              onclick: function () {
                updateChartTheme(this);
              }
            },
            {
              separator: true,
            },
            {
              text: "Download PDF document",
              onclick: function () {
                window._paq.push([
                  "trackEvent",
                  "Board DistrictExplorer",
                  "Downloaded: PDF in Scatter chart",
                ]);
                this.exportChart({
                  type: "application/pdf",
                });
              },
            },
            {
              text: "Download CSV",
              onclick: function () {
                window._paq.push([
                  "trackEvent",
                  "Board DistrictExplorer",
                  "Downloaded: CSV in Scatter chart",
                ]);
                this.downloadCSV();
              },
            },
            {
              text: "Download XLS",
              onclick: function () {
                window._paq.push([
                  "trackEvent",
                  "Board DistrictExplorer",
                  "Downloaded: XLS in Scatter chart",
                ]);
                this.downloadXLS();
              },
            },
          ],
        },
      },
      chartOptions: {
        chart: {
          backgroundColor: "#fff",
        },
      },
      csv: {
        columnHeaderFormatter: function (item, key) {
          if (!item || item instanceof Highcharts.Axis) {
            return x.label;
          } else {
            return item.name;
          }
        },
      },
    },
    plotOptions: {
      series: {
        point: {
          events: {
            mouseOver: function () {
              const labels = document.querySelector('.highcharts--scatter-chart div.highcharts-yaxis');
              labels?.classList?.add('to-back');
            },
            mouseOut: function () {
              const labels = document.querySelector('.highcharts--scatter-chart div.highcharts-yaxis');
              labels?.classList?.remove('to-back');
            }
          }
        },
        dataLabels: {
          enabled: false,
          format: '{point.district_name}',
          y:-5,
          overflow: true,
          crop: false,
          inside: false,
          allowOverlap: true,
        },
      },
    },
    series: [
      {
        keys: ["district_name"],
        name: "District",
        data: options.map( opt => {
          return {
            ...opt,
            color: opt.color ?? "#4646FF",
          }
        }),
      },
    ],
  };

  // Function to handle button click and update the chart
  const displayLabels = () => {
    if (chartRef.current) {
      let labelsEnabled = chartRef.current.chart.userOptions.plotOptions.series.dataLabels.enabled;
      chartRef.current.chart.update({
        plotOptions: {
          series: {
            dataLabels: {
              enabled: !labelsEnabled
            }
          }
        },
      });
    }
  };

  let showLabelsHelpText = "Names may overlap or disappear if too many points are being displayed"

  switch (options) {
    case "loading":
      return (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop:"10px" }}>
          <CircularProgress size={80}/>
        </Box>
      );
    case "no-data":
      return <div className="no-data">MISSING PARAMETER</div>;
    default:
      return (
        <Box sx={ScatterStyles} >
          <HighchartsReact highcharts={Highcharts} options={ScatterData} ref={chartRef}/>
          <div className={"scatter-tools"}>
            <FormGroup>
              <FormControlLabel className="control-labels" control={<Switch onChange={displayLabels} />} label="Show labels" />
            </FormGroup>
            <TooltipCustom
                text={showLabelsHelpText}
                aria-label={showLabelsHelpText}
            />
          </div>
        </Box>
      ) 
  }
};

ScatterChart.propTypes = {
  x: PropTypes.object.isRequired,
  xCustomRange: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
  ]),
  y: PropTypes.object.isRequired,
  yCustomRange: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
  ]),
  options: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
  ]),
}

export default React.memo(ScatterChart);
